import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { ClassNames } from "@emotion/react";
import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

import Button from "components/ui/button/Button";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import { EllipsisTooltipText } from "lib/core_components/EllipsisTooltip";
import { useAiChats } from "lib/hooks/api/ai_chat/useGetAiChats";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { EventNames, useTracking } from "lib/tracking";
import { AiChatsResourceType } from "lib/types/graphQLEnums";
import { AiChatResourceType, AIQuestionForm } from "./types";

function getResourceTypeEnum(resourceType: AiChatResourceType): AiChatsResourceType {
  switch (resourceType) {
    case "BUYER_QA":
      return AiChatsResourceType.BuyerQa;
    case "BID_ASK_AI":
      return AiChatsResourceType.BidAskAi;
    case "BID_WRITING_QA":
      return AiChatsResourceType.BidWritingQa;
    case "CONTENT_LIBRARY_QA":
      return AiChatsResourceType.ContentLibraryQa;
  }
}

type ChatsHistoryProps = {
  resourceId?: string;
  resourceName: string;
  resourceType: AiChatResourceType;
  onNewChatClick: () => void;
  selectedConversationId?: string;
  setSelectedConversationId: (conversationId: string) => void;
};

export default function AiChatHistory({
  resourceId,
  resourceName,
  resourceType,
  onNewChatClick,
  selectedConversationId,
  setSelectedConversationId,
}: ChatsHistoryProps) {
  const { logEvent } = useTracking();

  const { setValue } = useFormContext<AIQuestionForm>();

  const { data: chats, isLoading: isLoading } = useAiChats({
    resourceId,
    resourceType: getResourceTypeEnum(resourceType),
  });

  useEffect(() => {
    if (chats) {
      setValue("conversations", chats as AIQuestionForm["conversations"]);
    }
  }, [chats, setValue]);

  return (
    <ChatsHistoryContainer>
      <NewChatButtonContainer>
        <TransparentButton buttonType="tertiary" onClick={onNewChatClick} fullWidth>
          <Flex justifyContent="space-between" alignItems="center" width="100%">
            New Chat
            <UIcon icon="edit02" />
          </Flex>
        </TransparentButton>
      </NewChatButtonContainer>
      <OldChatsContainer>
        {isLoading && (
          <>
            <SkeletonText width="100%" />
            <SkeletonText width="100%" />
            <SkeletonText width="100%" />
          </>
        )}
        {chats &&
          chats.length > 0 &&
          chats.map(({ conversationId, questionAndAnswer }) => {
            const { question } = questionAndAnswer[0];

            return (
              <div key={conversationId}>
                <TransparentButton
                  buttonType="tertiary"
                  onClick={() => {
                    logEvent(EventNames.chatSelected, {
                      "Chat name": question,
                      "Chat Id": conversationId,
                      "Resource Id": resourceId,
                      "Resource name": resourceName,
                      "Context source": "Ask AI",
                    });
                    setSelectedConversationId(conversationId);
                  }}
                >
                  <ClassNames>
                    {({ css, theme }) => (
                      <EllipsisTooltipText
                        fullText={question}
                        containerClassname={css({
                          textWrap: "nowrap",
                          width: 182,
                          color:
                            selectedConversationId === conversationId
                              ? theme.colors.sysPrimaryDefault
                              : theme.colors.sysTextBlack,
                        })}
                      />
                    )}
                  </ClassNames>
                </TransparentButton>
              </div>
            );
          })}
      </OldChatsContainer>
    </ChatsHistoryContainer>
  );
}

const ChatsHistoryContainer = styled(Flex)({
  flexDirection: "column",
  width: 240,
  flexShrink: 0,
  paddingRight: 16,
});

const NewChatButtonContainer = styled(Flex)(({ theme }) => ({
  borderBottom: "1px solid " + theme.colors.sysBorderDecorative,
  paddingBottom: 8,
}));

const OldChatsContainer = styled(Flex)({
  flexDirection: "column",
  gap: 16,
  overflowY: "auto",
  marginTop: 16,
});

const TransparentButton = styled(Button)({
  backgroundColor: "transparent",
});
